import React, { useMemo, useState } from 'react';
import { CapturePropertyRobotDTO } from 'business/models/Models';
import RobotSettingsInput from '../robotSettingsInput';
import { getAcquireField, getTimeWaitEndStateField } from '../../utils';
import InputWithToggleLabel from 'components/input/inputWithToggleLabel';
import {
  FrapModes,
  frapModePropName,
  frapPrefix,
  getFrapIntervalOfImagesPropName,
} from '../../constants';
import { FrapConfigType } from '../../types';

interface FRAPPostBleachProps {
  robotCaptureProperties: CapturePropertyRobotDTO;
  robotId: number;
  handleChange: (propertyName: string, value: any) => void;
  handleFieldToggle: (propertyName: string, value?: any) => void;
  enabledFields: string[];
  lightPath: string;
  frapConfig: FrapConfigType;
}

function FRAPPostBleach(props: FRAPPostBleachProps) {
  const {
    robotCaptureProperties,
    robotId,
    handleChange,
    handleFieldToggle,
    enabledFields,
    lightPath,
    frapConfig,
  } = props;
  const [intervalValue, setIntervalValue] = useState<string>('');
  const [selectedRow, setSelectedRow] = useState<number>(null);
  const prefix = lightPath?.toUpperCase() === 'FRAP' ? frapPrefix : '';

  const frapMode =
    robotCaptureProperties.data[robotId]?.[frapModePropName] ||
    robotCaptureProperties.data[robotId]?.[frapPrefix + frapModePropName] ||
    '';
  const currValue =
    robotCaptureProperties.data[robotId]?.[getFrapIntervalOfImagesPropName(prefix)] ||
    (frapConfig &&
      frapConfig?.[getFrapIntervalOfImagesPropName(prefix)]?.join(';') + ';');

  const handleAdd = () => {
    if (intervalValue) {
      handleChange(
        getFrapIntervalOfImagesPropName(prefix),
        currValue + intervalValue + ';',
      );
      setIntervalValue('');
    }
  };

  const handleModify = () => {
    if (selectedRow !== null && intervalValue !== '') {
      const valuesArray = currValue.split(';');
      valuesArray[selectedRow] = intervalValue;
      handleChange(getFrapIntervalOfImagesPropName(prefix), valuesArray.join(';'));
      setIntervalValue('');
    }
  };

  const handleRowClick = (index) => {
    setSelectedRow(index);
  };

  const handleRemove = () => {
    if (selectedRow !== null) {
      const valuesArray = currValue.split(';');
      valuesArray.splice(selectedRow, 1);
      handleChange(getFrapIntervalOfImagesPropName(prefix), valuesArray.join(';'));
      setSelectedRow(null);
    }
  };

  const handleMoveUp = () => {
    if (selectedRow > 0) {
      const valuesArray = currValue.split(';');
      const temp = valuesArray[selectedRow];
      valuesArray[selectedRow] = valuesArray[selectedRow - 1];
      valuesArray[selectedRow - 1] = temp;
      handleChange(getFrapIntervalOfImagesPropName(prefix), valuesArray.join(';'));
      setSelectedRow(selectedRow - 1);
    }
  };

  const handleMoveDown = () => {
    const valuesArray = currValue.split(';');
    if (selectedRow !== null && selectedRow < valuesArray.length - 2) {
      const temp = valuesArray[selectedRow];
      valuesArray[selectedRow] = valuesArray[selectedRow + 1];
      valuesArray[selectedRow + 1] = temp;
      handleChange(getFrapIntervalOfImagesPropName(prefix), valuesArray.join(';'));
      setSelectedRow(selectedRow + 1);
    }
  };

  const displayValues = currValue
    ?.split(';')
    .filter((v) => v !== '')
    .map((value, index) => (
      <tr
        key={index}
        className={selectedRow === index ? 'bg-gray-200' : ''}
        onClick={() => handleRowClick(index)}
      >
        <td>{index + 1}</td>
        <td>{value}</td>
      </tr>
    ));

  const total = useMemo(() => {
    const sum = currValue
      ?.split(';')
      .filter((v) => v !== '')
      .reduce((a, v) => a + parseFloat(v), 0);

    const propertyName = getAcquireField(prefix).PropertyName;
    const propertyValue = parseFloat(
      robotCaptureProperties.data[robotId]?.[propertyName] || 0,
    );

    return (
      sum + (propertyValue || frapConfig?.[prefix + getAcquireField(prefix).PropertyName])
    );
  }, [currValue, robotCaptureProperties, robotId, prefix, getAcquireField]);

  const toggleField = (propertyName: string, value?: any) => {
    const defaultValue = frapConfig?.[propertyName];
    if (value === null || value === undefined || value === 'NaN') {
      if (propertyName === getAcquireField(prefix).PropertyName) {
        handleFieldToggle(propertyName, defaultValue);
        handleChange(getFrapIntervalOfImagesPropName(prefix), currValue);
      } else {
        handleFieldToggle(propertyName, defaultValue);
      }
    } else {
      handleFieldToggle(propertyName, value);
    }
  };

  const fullRecovery = useMemo(() => {
    return robotCaptureProperties.data[robotId]?.[getAcquireField(prefix).PropertyName]
      ? true
      : false;
  }, [robotCaptureProperties, robotId]);

  const changeFullRecovery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    const propertyName = getAcquireField(prefix).PropertyName;
    const defaultValue = frapConfig?.[propertyName];
    if (value) {
      handleFieldToggle(propertyName, defaultValue ?? true);
      handleChange(getFrapIntervalOfImagesPropName(prefix), currValue ?? '');
    } else {
      handleFieldToggle(propertyName, false);
      handleChange(getFrapIntervalOfImagesPropName(prefix), false);
    }
  };

  return (
    <>
      {frapMode === FrapModes.FullRecovery ? (
        <div
          id={`frappostbleach-${robotId}-frapmode-fullrecovery-wrapper`}
          className="border p-2 flex flex-col gap-2"
        >
          <label
            id={`frappostbleach-${robotId}-frapmode-fullrecovery-label`}
            className="flex gap-2 items-center h-full mt-1.5"
          >
            <input
              id={`frappostbleach-${robotId}-frapmode-fullrecovery-label-checkbox`}
              type="checkbox"
              checked={fullRecovery}
              onChange={changeFullRecovery}
              className="w-4 h-4 text-blue-600 bg-gray-100 shrink-0"
            />
            <span
              id={`frappostbleach-${robotId}-frapmode-fullrecovery-label-text`}
              className="flex flex-row gap-2 items-center"
            >
              Full Recovery FRAP Post-bleach Options
            </span>
          </label>
          <RobotSettingsInput
            key={1}
            field={getAcquireField(prefix)}
            value={
              robotCaptureProperties.data[robotId]?.[getAcquireField(prefix).PropertyName]
            }
            onChange={handleChange}
            onToggle={toggleField}
            enabled={enabledFields.includes(getAcquireField(prefix).PropertyName)}
            formData={robotCaptureProperties.data[robotId]}
            placeholder={frapConfig?.[getAcquireField(prefix).PropertyName]}
            showToggle={false}
            customClassName="!w-[80%]"
          />
          <fieldset
            id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset`}
            className="flex flex-col gap-2 disabled:opacity-50 disabled:pointer-events-none"
            disabled={!enabledFields.includes(getAcquireField(prefix).PropertyName)}
          >
            Acquire additional images at the following interval(s):
            <div
              id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-top-wrapper`}
              className="flex flex-row w-[40%] gap-2"
            >
              <InputWithToggleLabel
                type="text"
                unitLabel="s"
                value={intervalValue}
                label="Interval"
                handleChange={(e) => setIntervalValue(e.target.value)}
              />
              <button
                id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-add-button`}
                className="bg-gray-100 hover:bg-gray-200"
                onClick={handleAdd}
              >
                Add
              </button>
              <button
                id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-modify-button`}
                className="bg-gray-100 hover:bg-gray-200"
                onClick={handleModify}
              >
                Modify
              </button>
            </div>
            <div
              id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-bottom-wrapper`}
              className="flex flex-row gap-2 justify-start"
            >
              <div
                id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-table`}
                className="w-[35%] border h-28 overflow-auto"
              >
                <table
                  id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-table-wrapper`}
                  className="w-full"
                >
                  <thead
                    id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-table-header`}
                  >
                    <tr
                      id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-table-header-row`}
                    >
                      <th
                        id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-table-header-index`}
                        className="w-2/5 border"
                      >
                        Index
                      </th>
                      <th
                        id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-table-header-interval`}
                        className="w-3/5 border"
                      >
                        Interval
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-table-body-display`}
                  >
                    {displayValues}
                  </tbody>
                </table>
              </div>
              <div
                id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-bottom-buttons`}
                className="flex flex-col gap-2"
              >
                <button
                  id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-remove-button`}
                  className="bg-gray-100 hover:bg-gray-200"
                  onClick={handleRemove}
                  disabled={selectedRow === null}
                >
                  Remove
                </button>
                <button
                  id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-moveup-button`}
                  className="bg-gray-100 hover:bg-gray-200"
                  onClick={handleMoveUp}
                  disabled={selectedRow === null}
                >
                  Move Up
                </button>
                <button
                  id={`frappostbleach-${robotId}-frapmode-fullrecovery-fieldset-movedown-button`}
                  className="bg-gray-100 hover:bg-gray-200"
                  onClick={handleMoveDown}
                  disabled={selectedRow === null}
                >
                  Move Down
                </button>
              </div>
            </div>
          </fieldset>
          <div
            id={`frappostbleach-${robotId}-frapmode-fullrecovery-imaging-time`}
            className="flex flex-row w-[60%]"
          >
            <InputWithToggleLabel
              type="text"
              unitLabel="s"
              value={total}
              label="Post-bleach imaging time:"
              handleChange={undefined}
              disabled
            />
          </div>
        </div>
      ) : null}
      {frapMode === FrapModes.HighThroughput ? (
        <div
          id={`frappostbleach-${robotId}-frapmode-highthroughput-wrapper`}
          className="border p-2 flex flex-col gap-2"
        >
          HT FRAP Post-bleach Options <br />
          <RobotSettingsInput
            key={2}
            field={getTimeWaitEndStateField(prefix)}
            value={
              robotCaptureProperties.data[robotId]?.[
                getTimeWaitEndStateField(prefix).PropertyName
              ]
            }
            onChange={handleChange}
            onToggle={toggleField}
            enabled={enabledFields.includes(
              getTimeWaitEndStateField(prefix).PropertyName,
            )}
            formData={robotCaptureProperties.data[robotId]}
            placeholder={frapConfig?.[getTimeWaitEndStateField(prefix).PropertyName]}
            customClassName="!w-[100%]"
          />
        </div>
      ) : null}
    </>
  );
}

export default FRAPPostBleach;
